import { mskDateString } from "@/lib/refresh/dates";

/**
 * Колонка «Заказы по неделям»: календарь заказов товара по дням, собранный в
 * недели.
 *
 * В сохранённом наборе колонок хранится ОДИН маркер `orders_weeks`, а при показе
 * он разворачивается в живой набор (как «Акции», lib/promo-actions-columns):
 *
 *   [Неделя][пн 24]…[вс 30]  [Неделя][пн 31]…[вс 6]  [Неделя][пн 7]…[вс 13]  [Неделя][пн 14][вт 15]
 *    ── полная ──────────────  ── полная ────────────  ── полная ────────────  ── текущая, неполная ──
 *
 * Недели календарные, пн–вс по Москве. Дни — только прошедшие целиком: последний
 * день — вчера (`ordersWeeksAnchorDate`), сегодняшний неполный день не
 * показывается. Всегда три полные недели плюс текущая, пока в ней есть хотя бы
 * один прошедший день; в понедельник текущей недели ещё нет — вчерашнее
 * воскресенье закрыло третью полную.
 *
 * Колонка недели стоит ПЕРВОЙ в своей неделе: группы колонок таблицы
 * сворачиваются до первой колонки слева (buildColumnGroupDisclosurePlan), и
 * свёрнутая неделя оставляет ровно сумму.
 *
 * Числа — из `daily_orders_30d` товара (все заказы по дате создания,
 * [длина − 1] = вчера): 3 недели + 6 дней = 27 ≤ 30, новых запросов не нужно.
 */

export const ORDERS_WEEKS_MARKER_KEY = "orders_weeks";
/** Префиксы ключей дня и недели; остальные календари (lib/week-calendars)
 *  строят свои ключи по тому же образцу: `<метрика>_week_day:` / `_sum:`. */
export const ORDERS_WEEK_DAY_PREFIX = "orders_week_day:";
export const ORDERS_WEEK_SUM_PREFIX = "orders_week_sum:";
const DAY_PREFIX = ORDERS_WEEK_DAY_PREFIX;
const SUM_PREFIX = ORDERS_WEEK_SUM_PREFIX;

/** Сколько полных недель показывать помимо текущей. */
export const ORDERS_WEEKS_FULL_COUNT = 3;
/** Дней дневника, которые нужны колонке: 3 недели + 6 дней текущей + сегодня. */
export const ORDERS_WEEKS_DIARY_DAYS = ORDERS_WEEKS_FULL_COUNT * 7 + 7;

/** День: число и мелкая разница к прошлому дню в слоте («10 +10») — как у «Заказов 30д». */
export const ORDERS_WEEK_DAY_COLUMN_WIDTH = 58;
export const ORDERS_WEEK_SUM_COLUMN_WIDTH = 74;
export const ORDERS_WEEK_COLUMN_MIN_WIDTH = 36;

/** Подпись группы текущей недели — по ней таблица знает, что раскрыть по умолчанию. */
export const ORDERS_CURRENT_WEEK_GROUP_LABEL = "Эта неделя";
export const ORDERS_WEEK_SUM_LABEL = "Неделя";

/** Фон колонки суммы недели и её полосы в шапке — чуть темнее дней. */
export const ORDERS_WEEK_SUM_TINT = "#EDEAE1";

/**
 * Тепло дня: фон ячейки тем гуще, чем больше заказов относительно максимума.
 * По умолчанию максимум — лучший день этой строки за окно календаря (каждая
 * строка читается своей картой продаж); при включённой «Тепловой карте»
 * колонки — самый сильный размер той же модели в этот день («кто сделал
 * день»). Сланцево-синий, а не зелёный: зелёный в таблице занят заказами.
 */
const ORDERS_WEEK_HEAT_RGB = "140,166,196";
/** `strong` — строка модели: гуще размеров, иначе сливается с ними; сами
 *  размеры светлые (до 0,32), чтобы раскрытая модель не пестрила. */
export function ordersWeekHeatColor(value: number, max: number, strong = false): string | undefined {
  if (value <= 0 || max <= 0) return undefined;
  const share = Math.min(1, value / max);
  const alpha = strong ? 0.16 + share * 0.46 : 0.05 + share * 0.27;
  return `rgba(${ORDERS_WEEK_HEAT_RGB},${alpha.toFixed(2)})`;
}

/**
 * Выделяющееся значение дня — полужирным: заметно выше среднего среди тех, с
 * кем сравниваем В ЭТОТ ДЕНЬ (размеры одной модели; у товара без размеров —
 * все товары таблицы с заказами в этот день). Порог 1,3× от среднего и не
 * меньше двух заказов: единица над средним 0,6 — не событие.
 */
export function isOrdersWeekStandout(value: number, peerAverage: number): boolean {
  return value >= 2 && peerAverage > 0 && value >= peerAverage * 1.3;
}

/** Максимум заказов по дням окна календаря у одного товара (для тепла без размеров). */
export function ordersWeekRowMax(daily: readonly number[] | null | undefined, days = ORDERS_WEEKS_DIARY_DAYS - 1): number {
  if (!daily || daily.length === 0) return 0;
  return daily.slice(-days).reduce((max, value) => Math.max(max, Number(value) || 0), 0);
}

const DAY_MS = 86_400_000;
const WEEKDAYS_SHORT = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const WEEKDAYS_LONG = ["воскресенье", "понедельник", "вторник", "среда", "четверг", "пятница", "суббота"];
const MONTHS_SHORT = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const MONTHS_GENITIVE = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];

export type OrdersWeek = {
  /** Понедельник недели, YYYY-MM-DD. */
  start: string;
  /** Воскресенье недели, YYYY-MM-DD — даже если данных до него ещё нет. */
  end: string;
  /** Дни с данными: от понедельника до последнего прошедшего дня. */
  days: string[];
  /** Текущая неполная неделя. */
  current: boolean;
};

function isoToUtcMs(date: string): number {
  const [year, month, day] = date.split("-").map(Number);
  return Date.UTC(year, month - 1, day);
}

function utcMsToIso(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

export function isIsoDateKey(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(isoToUtcMs(value));
}

/** День недели: 0 = воскресенье … 6 = суббота (как у Date, но от даты, не от зоны). */
export function ordersWeekdayIndex(date: string): number {
  return new Date(isoToUtcMs(date)).getUTCDay();
}

/** Понедельник недели, в которую попадает дата. */
export function ordersWeekStartOf(date: string): string {
  const weekday = ordersWeekdayIndex(date);
  const daysSinceMonday = (weekday + 6) % 7;
  return utcMsToIso(isoToUtcMs(date) - daysSinceMonday * DAY_MS);
}

export function daysBetweenIso(from: string, to: string): number {
  return Math.round((isoToUtcMs(to) - isoToUtcMs(from)) / DAY_MS);
}

export function addDaysIso(date: string, days: number): string {
  return utcMsToIso(isoToUtcMs(date) + days * DAY_MS);
}

/** Последний день с данными — вчера по Москве. */
export function ordersWeeksAnchorDate(nowMs = Date.now()): string {
  return mskDateString(nowMs - DAY_MS);
}

/**
 * Недели для показа: три полные и текущая, если в ней уже есть прошедшие дни.
 * Порядок — от старой к новой, как в таблице слева направо.
 */
export function buildOrdersWeeks(anchorDate: string): OrdersWeek[] {
  const anchorWeekStart = ordersWeekStartOf(anchorDate);
  const anchorIsSunday = ordersWeekdayIndex(anchorDate) === 0;
  // Вчера — воскресенье: его неделя полная и становится третьей; текущей нет.
  const lastFullStart = anchorIsSunday ? anchorWeekStart : addDaysIso(anchorWeekStart, -7);
  const weeks: OrdersWeek[] = [];
  for (let index = ORDERS_WEEKS_FULL_COUNT - 1; index >= 0; index--) {
    const start = addDaysIso(lastFullStart, -7 * index);
    weeks.push({
      start,
      end: addDaysIso(start, 6),
      days: Array.from({ length: 7 }, (_, offset) => addDaysIso(start, offset)),
      current: false,
    });
  }
  if (!anchorIsSunday) {
    const length = daysBetweenIso(anchorWeekStart, anchorDate) + 1;
    weeks.push({
      start: anchorWeekStart,
      end: addDaysIso(anchorWeekStart, 6),
      days: Array.from({ length }, (_, offset) => addDaysIso(anchorWeekStart, offset)),
      current: true,
    });
  }
  return weeks;
}

export function ordersWeekDayColumnKey(date: string): string {
  return `${DAY_PREFIX}${date}`;
}

export function ordersWeekSumColumnKey(weekStart: string): string {
  return `${SUM_PREFIX}${weekStart}`;
}

export function parseOrdersWeekDayKey(key: string): string | null {
  if (!key.startsWith(DAY_PREFIX)) return null;
  const date = key.slice(DAY_PREFIX.length);
  return isIsoDateKey(date) ? date : null;
}

export function parseOrdersWeekSumKey(key: string): string | null {
  if (!key.startsWith(SUM_PREFIX)) return null;
  const date = key.slice(SUM_PREFIX.length);
  return isIsoDateKey(date) ? date : null;
}

export function isOrdersWeekDayColumnKey(key: string): boolean {
  return parseOrdersWeekDayKey(key) !== null;
}

export function isOrdersWeekSumColumnKey(key: string): boolean {
  return parseOrdersWeekSumKey(key) !== null;
}

/** Любая колонка календаря: день или сумма недели (маркер — нет). */
export function isOrdersWeekColumnKey(key: string): boolean {
  return isOrdersWeekDayColumnKey(key) || isOrdersWeekSumColumnKey(key);
}

/** Понедельник недели, к которой относится колонка дня или суммы. */
export function ordersWeekStartOfColumnKey(key: string): string | null {
  const sumStart = parseOrdersWeekSumKey(key);
  if (sumStart) return sumStart;
  const day = parseOrdersWeekDayKey(key);
  return day ? ordersWeekStartOf(day) : null;
}

/** Колонки недели в порядке показа: сначала сумма, затем дни. */
export function ordersWeekColumnKeys(week: OrdersWeek): string[] {
  return [ordersWeekSumColumnKey(week.start), ...week.days.map(ordersWeekDayColumnKey)];
}

/**
 * Маркер → колонки дней и недель на его месте. Уже развёрнутые ключи (например,
 * из старого сохранения) остаются на своих местах без дублей.
 */
export function expandOrdersWeeksColumns(columns: readonly string[], anchorDate: string): string[] {
  if (!columns.includes(ORDERS_WEEKS_MARKER_KEY)) return [...columns];
  const weekKeys = buildOrdersWeeks(anchorDate).flatMap(ordersWeekColumnKeys);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const key of columns) {
    if (key === ORDERS_WEEKS_MARKER_KEY) {
      for (const weekKey of weekKeys) if (!seen.has(weekKey)) { seen.add(weekKey); out.push(weekKey); }
    } else if (!seen.has(key)) {
      seen.add(key);
      out.push(key);
    }
  }
  return out;
}

/** Колонки дней и недель → один маркер на месте первой из них (для редактора). */
export function collapseOrdersWeeksColumns(columns: readonly string[]): string[] {
  const out: string[] = [];
  let collapsed = false;
  for (const key of columns) {
    if (isOrdersWeekColumnKey(key)) {
      if (!collapsed) {
        out.push(ORDERS_WEEKS_MARKER_KEY);
        collapsed = true;
      }
      continue;
    }
    if (!out.includes(key)) out.push(key);
  }
  return out;
}

// ── Значения ────────────────────────────────────────────────────────────────

/**
 * Заказы за день из ряда `daily_orders_30d`: последний элемент — вчера
 * (`anchorDate`), дальше в прошлое по одному дню. Дня нет в ряду — 0.
 */
export function ordersOnDate(daily: readonly number[] | null | undefined, date: string, anchorDate: string): number {
  if (!daily || daily.length === 0) return 0;
  const index = daily.length - 1 - daysBetweenIso(date, anchorDate);
  if (index < 0 || index >= daily.length) return 0;
  return Number(daily[index]) || 0;
}

/**
 * Разница с предыдущим днём (как «+5» у «Заказов 30д»): null, если прошлого
 * дня нет в ряду — у самого старого дня окна.
 */
export function ordersDayDelta(daily: readonly number[] | null | undefined, date: string, anchorDate: string): number | null {
  if (!daily || daily.length === 0) return null;
  const previous = addDaysIso(date, -1);
  const previousIndex = daily.length - 1 - daysBetweenIso(previous, anchorDate);
  if (previousIndex < 0 || previousIndex >= daily.length) return null;
  return ordersOnDate(daily, date, anchorDate) - (Number(daily[previousIndex]) || 0);
}

/** Заказы за неделю: сумма по её прошедшим дням (до `anchorDate`). */
export function ordersInWeek(daily: readonly number[] | null | undefined, weekStart: string, anchorDate: string): number {
  let total = 0;
  for (let offset = 0; offset < 7; offset++) {
    const date = addDaysIso(weekStart, offset);
    if (date > anchorDate) break;
    total += ordersOnDate(daily, date, anchorDate);
  }
  return total;
}

/**
 * Разница с прошлой неделей за те же дни: полная неделя сравнивается с полной,
 * текущая (3 дня) — с первыми тремя днями прошлой. null — прошлой недели нет
 * в 30-дневном ряду целиком (у самой старой недели календаря), тогда стрелку
 * не показываем, чем сравнивать с обрезком.
 */
export function ordersWeekDelta(daily: readonly number[] | null | undefined, weekStart: string, anchorDate: string): number | null {
  if (!daily || daily.length === 0) return null;
  const elapsed = ordersWeekElapsedDays(weekStart, anchorDate);
  if (elapsed === 0) return null;
  const previousStart = addDaysIso(weekStart, -7);
  const oldestIndex = daily.length - 1 - daysBetweenIso(previousStart, anchorDate);
  if (oldestIndex < 0) return null;
  let previous = 0;
  for (let offset = 0; offset < elapsed; offset++) previous += ordersOnDate(daily, addDaysIso(previousStart, offset), anchorDate);
  return ordersInWeek(daily, weekStart, anchorDate) - previous;
}

/** Сколько дней недели уже прошло (для подписи «3 дня» у текущей). */
export function ordersWeekElapsedDays(weekStart: string, anchorDate: string): number {
  return Math.max(0, Math.min(7, daysBetweenIso(weekStart, anchorDate) + 1));
}

// ── Подписи ─────────────────────────────────────────────────────────────────

/** Шапка колонки дня: «пн 7». */
export function ordersWeekDayLabel(date: string): string {
  const [, , day] = date.split("-").map(Number);
  return `${WEEKDAYS_SHORT[ordersWeekdayIndex(date)]} ${day}`;
}

/** Полная дата для подсказок: «понедельник, 7 сентября». */
export function ordersWeekDayTitle(date: string): string {
  const [, month, day] = date.split("-").map(Number);
  return `${WEEKDAYS_LONG[ordersWeekdayIndex(date)]}, ${day} ${MONTHS_GENITIVE[month - 1]}`;
}

/** Диапазон недели: «7–13 сен» или «31 авг – 6 сен», если неделя через месяц. */
export function ordersWeekRangeLabel(weekStart: string): string {
  const end = addDaysIso(weekStart, 6);
  const [, startMonth, startDay] = weekStart.split("-").map(Number);
  const [, endMonth, endDay] = end.split("-").map(Number);
  if (startMonth === endMonth) return `${startDay}–${endDay} ${MONTHS_SHORT[startMonth - 1]}`;
  return `${startDay} ${MONTHS_SHORT[startMonth - 1]} – ${endDay} ${MONTHS_SHORT[endMonth - 1]}`;
}

/** Подпись полосы группы над неделей: даты у полных, «Эта неделя» у текущей. */
export function ordersWeekGroupLabel(weekStart: string, anchorDate: string): string {
  return ordersWeekStartOf(anchorDate) === weekStart && ordersWeekdayIndex(anchorDate) !== 0
    ? ORDERS_CURRENT_WEEK_GROUP_LABEL
    : ordersWeekRangeLabel(weekStart);
}

/**
 * Какие группы недель раскрыты по умолчанию: текущая неделя, а когда её ещё
 * нет (вчера было воскресенье) — последняя полная, иначе по понедельникам
 * календарь показывал бы три суммы и ни одного дня.
 */
export function ordersWeeksDefaultExpandedGroups(anchorDate: string): string[] {
  const weeks = buildOrdersWeeks(anchorDate);
  const last = weeks[weeks.length - 1];
  return last?.current ? [ORDERS_CURRENT_WEEK_GROUP_LABEL] : last ? [ordersWeekRangeLabel(last.start)] : [];
}

export function isOrdersWeekend(date: string): boolean {
  const weekday = ordersWeekdayIndex(date);
  return weekday === 0 || weekday === 6;
}
